/**
 * src/lib/pwa/swUpdate.js — detect when a newer DEPLOYED build is available.
 *
 * Why this exists (TICKET-089): the CEO keeps the installed PWA open and checks
 * 20+/day. The service worker (public/sw.js) is network-first + skipWaiting, so
 * a fresh launch DOES pull the new bundle — but an already-open session can sit
 * on the old in-memory bundle until it happens to be killed. That caused a full
 * false-alarm debugging cycle on TICKET-087 (the fix was live; the CEO was on a
 * pre-fix build). main.jsx previously auto-RELOADED on controllerchange, which
 * can yank the user mid-action; this replaces that with a visible "New version —
 * Reload" banner (UpdateBanner) the user controls.
 *
 * `watchForSwUpdate(onUpdateAvailable, opts)` fires the callback ONCE when an
 * UPDATED service worker is ready (not on first install), and periodically asks
 * the registration to re-check so a deploy that happens while the app is open is
 * noticed. Returns a cleanup function. The serviceWorker container is injectable
 * for unit testing.
 */
/* ── Update-reload bookkeeping (TICKET-771) ────────────────────────────────
 * CEO 2026-09-05, mid-UAT: "I reload over and over the page and don't get past
 * the new version banner." Two failure modes produce that loop, and BOTH are
 * fixed here because the banner cannot tell them apart from inside the page:
 *  (a) a waiting worker that never activates — a plain reload does NOT release
 *      the old controller while a client stays open, so the update sits there
 *      and re-announces itself on every single load, forever;
 *  (b) an edge/CDN serving two builds' sw.js in alternation, so each check
 *      installs "a new version" again.
 * A user-initiated update reload therefore (1) tells the waiting worker to
 * skipWaiting so it actually takes over, and (2) starts a short cooldown during
 * which the banner stays quiet. Worst case the CEO learns about a genuinely new
 * deploy COOLDOWN_MS late; the loop is impossible.
 */
const RELOAD_KEY = 'acn_sw_update_reloaded_at';
export const UPDATE_COOLDOWN_MS = 5 * 60 * 1000;

function safeStore(storage) {
  if (storage) return storage;
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

/** Milliseconds left in the quiet period; 0 when it is over (or never started). */
export function cooldownRemainingMs({ storage, now = Date.now() } = {}) {
  const s = safeStore(storage);
  if (!s) return 0;
  try {
    const at = parseInt(s.getItem(RELOAD_KEY) || '', 10);
    if (!Number.isFinite(at)) return 0;
    // A clock that jumped backwards would otherwise silence the banner forever.
    if (at > now) { s.removeItem(RELOAD_KEY); return 0; }
    return Math.max(0, UPDATE_COOLDOWN_MS - (now - at));
  } catch { return 0; }
}

/** True while a just-tapped update reload should keep the banner quiet. */
export function inUpdateCooldown(args = {}) {
  return cooldownRemainingMs(args) > 0;
}

/** Stamp the cooldown. Called right before an update reload. */
export function markUpdateReload({ storage, now = Date.now() } = {}) {
  const s = safeStore(storage);
  if (!s) return;
  try { s.setItem(RELOAD_KEY, String(now)); } catch { /* private mode — best-effort */ }
}

/**
 * Drop the stamp (TICKET-776). Called once the signals caused by the user's own
 * reload have been consumed, so a genuinely NEW deploy arriving a minute later
 * is announced immediately instead of waiting out the rest of the window.
 */
export function clearUpdateReload({ storage } = {}) {
  const s = safeStore(storage);
  if (!s) return;
  try { s.removeItem(RELOAD_KEY); } catch { /* private mode — best-effort */ }
}

/**
 * Apply a pending update, then reload. Posting SKIP_WAITING is what makes the
 * reload STICK: without it the waiting worker keeps waiting (an open client
 * blocks the handover) and the next load announces the same update again.
 * Fire-and-forget — the reload happens either way.
 */
export async function applySwUpdate(opts = {}) {
  const {
    swContainer = (typeof navigator !== 'undefined' ? navigator.serviceWorker : null),
    reload = () => { try { window.location.reload(); } catch { /* noop */ } },
    storage,
    now = Date.now(),
  } = opts;

  markUpdateReload({ storage, now });
  try {
    const reg = await Promise.resolve(swContainer?.getRegistration?.());
    reg?.waiting?.postMessage?.({ type: 'SKIP_WAITING' });
  } catch { /* an un-messageable worker still gets the reload below */ }
  reload();
}

export function watchForSwUpdate(onUpdateAvailable, opts = {}) {
  const {
    swContainer = (typeof navigator !== 'undefined' ? navigator.serviceWorker : null),
    doc = (typeof document !== 'undefined' ? document : null),
    intervalMs = 60000,
  } = opts;

  if (!swContainer || typeof swContainer.getRegistration !== 'function') {
    return () => {};
  }

  let notified = false;
  let deferTimer = null;
  let interval = null;
  let reg = null;
  let sawInstall = false;
  const hadController = !!swContainer.controller;

  const notify = () => {
    if (notified) return;
    // TICKET-771: a reload the user JUST performed must not re-announce itself.
    // TICKET-775 (Codex P1 on PR #1472): DEFER, never discard. 'installed' and
    // 'controllerchange' fire ONCE per worker — a genuine second deploy reaching
    // the page during the cooldown (exactly the rapid-deploy case this targets)
    // would otherwise lose its only signal and stay silent indefinitely, because
    // periodic registration.update() does not re-emit for a worker already current.
    const waitMs = cooldownRemainingMs({ storage: opts.storage });
    if (waitMs > 0) {
      if (deferTimer) return;
      deferTimer = setTimeout(() => { deferTimer = null; notify(); }, waitMs);
      return;
    }
    notified = true;
    // AEGIS P3-04: log thrown callback errors so future hooks/refusals are diagnosable
    // in Vercel edge logs (e.g. if React rules-of-hooks violation causes setState throw).
    try { onUpdateAvailable(); } catch (err) { console.warn('[swUpdate] onUpdateAvailable threw:', err); }
  };

  // A worker reaching 'installed' WHILE a controller already exists = an update
  // (first install has no prior controller, so it won't false-fire).
  const trackWorker = (worker) => {
    if (!worker) return;
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && swContainer.controller) {
        // TICKET-776: a worker that INSTALLED while this page was alive is the
        // only proof of a genuinely new build. Everything else during the
        // cooldown is an echo of the user's own reload.
        sawInstall = true;
        notify();
      }
    });
  };

  // The SW uses skipWaiting + clients.claim, so a new SW takes control and fires
  // controllerchange. Only treat it as an update if something controlled the page
  // before (guards against the first-install claim).
  const onControllerChange = () => {
    if (!hadController) return;
    // TICKET-776: during the cooldown, a takeover with no preceding install on
    // THIS page IS the handover the user's own reload asked for (applySwUpdate
    // posted SKIP_WAITING, then reloaded). Announcing it — even deferred to the
    // end of the window (TICKET-775) — re-shows the banner with no new build.
    // Consume the stamp so the NEXT deploy is announced at once.
    if (!sawInstall && cooldownRemainingMs({ storage: opts.storage }) > 0) {
      clearUpdateReload({ storage: opts.storage });
      return;
    }
    notify();
  };
  swContainer.addEventListener('controllerchange', onControllerChange);

  const onVisible = () => {
    if (doc && doc.visibilityState === 'visible' && reg) {
      Promise.resolve(reg.update?.()).catch(() => {});
    }
  };

  Promise.resolve(swContainer.getRegistration())
    .then((registration) => {
      if (!registration) return;
      reg = registration;
      if (registration.waiting && swContainer.controller) {
        // TICKET-776: still waiting right after the user's own update reload =
        // applySwUpdate's SKIP_WAITING raced the reload and was lost. Re-post it
        // silently; the takeover arrives as a controllerchange handled above.
        if (cooldownRemainingMs({ storage: opts.storage }) > 0) {
          try { registration.waiting.postMessage?.({ type: 'SKIP_WAITING' }); } catch { /* spine-emit-ok: an un-messageable worker just keeps waiting; the next load retries */ }
        } else {
          notify();
        }
      }
      trackWorker(registration.installing);
      registration.addEventListener('updatefound', () => trackWorker(registration.installing));
      interval = setInterval(() => { Promise.resolve(registration.update?.()).catch(() => {}); }, intervalMs);
      if (doc) doc.addEventListener('visibilitychange', onVisible);
    })
    .catch(() => {});

  return () => {
    if (interval) clearInterval(interval);
    if (deferTimer) { clearTimeout(deferTimer); deferTimer = null; }
    swContainer.removeEventListener('controllerchange', onControllerChange);
    if (doc) doc.removeEventListener('visibilitychange', onVisible);
  };
}
